import {useMemo, useCallback} from 'react'
import {IconButton, Box, Tooltip} from '@mui/material'
import UndoRounded from '@mui/icons-material/UndoRounded'
import RedoRounded from '@mui/icons-material/RedoRounded'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {undo, redo} from '../../../common/undoable'
import {trackEvent} from '../../../common/analytics/trackEvent'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

// ── UndoRedoToolbar ───────────────────────────────────────────────

const UndoRedoToolbar = () => {
  const dispatch = useAppDispatch()
  const tokens = useAppTokens()
  const {t} = useTranslation()
  const canUndo = useAppSelector((s) => s.past.length > 0)
  const canRedo = useAppSelector((s) => s.future.length > 0)

  const handleUndo = useCallback(() => {
    trackEvent('undo_clicked', {via: 'button'})
    dispatch(undo())
  }, [dispatch])
  const handleRedo = useCallback(() => {
    trackEvent('redo_clicked', {via: 'button'})
    dispatch(redo())
  }, [dispatch])

  const sx = useMemo(() => buildStyles(tokens), [tokens])

  if (!canUndo && !canRedo) return null

  return (
    <Box sx={sx.root}>
      <Tooltip title={`${t('a11y.undo')} (⌘Z)`} placement="bottom">
        <span>
          <IconButton size="small" disabled={!canUndo} onClick={handleUndo} sx={sx.button}>
            <UndoRounded sx={sx.icon} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={`${t('a11y.redo')} (⌘⇧Z)`} placement="bottom">
        <span>
          <IconButton size="small" disabled={!canRedo} onClick={handleRedo} sx={sx.button}>
            <RedoRounded sx={sx.icon} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}

// ── Styles ────────────────────────────────────────────────────────

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    position: 'absolute' as const,
    top: 12,
    left: 12,
    zIndex: 20,
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    padding: '4px 6px',
    borderRadius: '10px',
    backgroundColor: tokens.surface.glass,
    backdropFilter: 'blur(12px)',
    border: `1px solid ${tokens.accordion.border}`,
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.3)',
  },
  button: {
    color: 'rgba(255, 255, 255, 0.75)',
    '&:hover': {
      color: 'rgba(255, 255, 255, 0.95)',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.2)',
    },
  },
  icon: {
    fontSize: 18,
  },
})

export default UndoRedoToolbar
